/**
 * Emerald Books — Revenue Recognition (ASC 606) UI.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Plus, Play, X, CheckCircle2, Trash2, PlayCircle, Calendar,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nextMonth = () => {
  const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toISOString().slice(0, 7);
};

export const RevRecSchedulesPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [batchPeriod, setBatchPeriod] = useState(nextMonth());
  const [form, setForm] = useState({
    source_no: '', customer_name: '', total_amount: '',
    currency: 'GBP', start_date: '', end_date: '',
    method: 'straight_line', note: '',
  });

  const load = async () => {
    setLoading(true);
    try { const r = await booksClient.get('/rev-rec/schedules'); setItems(unwrapList(r)); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await booksClient.post('/rev-rec/schedules', {
        ...form, total_amount: Number(form.total_amount || 0),
      });
      toast.success('Schedule drafted');
      setShowForm(false);
      setForm({ source_no: '', customer_name: '', total_amount: '', currency: 'GBP',
        start_date: '', end_date: '', method: 'straight_line', note: '' });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const activate = async (id) => {
    try { await booksClient.post(`/rev-rec/schedules/${id}/activate`); toast.success('Activated — AR/Deferred journal posted'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runMonth = async () => {
    if (!batchPeriod) return;
    try {
      const r = await booksClient.post('/rev-rec/run-month', null, { params: { period: batchPeriod } });
      toast.success(`Recognised ${r.data.recognised_count} · £${money(r.data.total_recognised)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm('Cancel this schedule?')) return;
    try { await booksClient.delete(`/rev-rec/schedules/${id}`); toast.success('Cancelled'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const recognisePeriod = async (id, period) => {
    try {
      const r = await booksClient.post(`/rev-rec/schedules/${id}/recognise-period`, null, { params: { period } });
      toast.success(`Recognised ${period} · ${money(r.data.amount)}`);
      const s = await booksClient.get(`/rev-rec/schedules/${id}`);
      setDetail(s.data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (id) => {
    try { const r = await booksClient.get(`/rev-rec/schedules/${id}`); setDetail(r.data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Revenue Recognition</h1>
          <p className="text-sm text-slate-500">ASC 606 · deferred revenue schedules · monthly recognition</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={batchPeriod} onChange={(e) => setBatchPeriod(e.target.value)} className="w-36" />
          <Button variant="outline" onClick={runMonth} data-testid="revrec-run-month-btn">
            <PlayCircle className="w-4 h-4 mr-1" /> Run month
          </Button>
          <Button onClick={() => setShowForm(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="revrec-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Schedule
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white p-4 rounded-lg border grid sm:grid-cols-2 gap-2">
          <Input placeholder="Contract / Invoice No" value={form.source_no} onChange={(e) => setForm({ ...form, source_no: e.target.value })} />
          <Input placeholder="Customer name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <Input type="number" step="0.01" placeholder="Total amount *" required value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} />
          <Input placeholder="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="px-3 py-2 border rounded text-sm">
            <option value="straight_line">Straight-line (equal per month)</option>
            <option value="point_in_time">Point-in-time (100% at start)</option>
            <option value="milestone">Milestone (manual per period)</option>
          </select>
          <Input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <Button type="submit" className="sm:col-span-2 bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="revrec-submit-btn">Save draft</Button>
        </form>
      )}

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="space-y-2" data-testid="revrec-list">
          {items.length === 0 && <div className="text-center text-slate-400 py-12">No schedules yet</div>}
          {items.map((s) => {
            const recognised = (s.periods || []).filter((p) => p.status === 'recognised').length;
            const pct = s.periods?.length ? Math.round((recognised / s.periods.length) * 100) : 0;
            return (
              <div key={s.schedule_id} className="bg-white rounded-lg border p-3" data-testid={`revrec-item-${s.schedule_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openDetail(s.schedule_id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                        s.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                        s.status === 'draft' ? 'bg-slate-100 text-slate-600' :
                        s.status === 'completed' ? 'bg-blue-50 text-blue-700' : 'bg-rose-50 text-rose-700'
                      }`}>{s.status}</span>
                      <span className="text-xs uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {s.method?.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="mt-1 font-semibold text-slate-800">
                      {s.customer_name} — {money(s.total_amount)} {s.currency}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {s.source_no ? `${s.source_no} · ` : ''}{s.start_date} → {s.end_date} · {s.periods?.length || 0} periods · {recognised}/{s.periods?.length} recognised
                    </div>
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {s.status === 'draft' && (
                      <button onClick={() => activate(s.schedule_id)} className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Activate" data-testid={`revrec-activate-${s.schedule_id}`}>
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {s.status !== 'completed' && s.status !== 'cancelled' && (
                      <button onClick={() => cancel(s.schedule_id)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Cancel">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-5 w-full max-w-2xl space-y-3 my-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-400">{detail.source_no}</div>
                <div className="font-semibold text-slate-800">{detail.customer_name} · {money(detail.total_amount)} {detail.currency}</div>
              </div>
              <button onClick={() => setDetail(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-96 overflow-y-auto">
              {(detail.periods || []).map((p) => (
                <div key={p.period} className={`p-2 rounded border text-sm flex items-center justify-between ${p.status === 'recognised' ? 'bg-emerald-50 border-emerald-200' : 'bg-white'}`}>
                  <div>
                    <div className="font-mono text-xs text-slate-500">{p.period}</div>
                    <div className="font-semibold">{money(p.amount)}</div>
                  </div>
                  {p.status === 'recognised' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : (
                    detail.status === 'active' ? (
                      <button onClick={() => recognisePeriod(detail.schedule_id, p.period)}
                        className="text-xs px-2 py-1 rounded bg-[#0f5c3d] text-white">Recognise</button>
                    ) : <Calendar className="w-4 h-4 text-slate-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevRecSchedulesPage;
